import React, { useState, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChangePasswordRequest, validatePasswordPolicy, PasswordPolicyViolationType } from '@df-gate-share/shared';
import { useAuth } from '../contexts/AuthContext';
import { apiClient, getErrorMessage } from '../lib/api';
import MkdfLogo from '../components/MkdfLogo';
import DFFilesLogo from '../components/DFFilesLogo';

const POLICY_LABELS: Record<PasswordPolicyViolationType, string> = {
  minLength: '8文字以上',
  maxLength: '128文字以内',
  uppercase: '大文字(A-Z)',
  lowercase: '小文字(a-z)',
  digit: '数字(0-9)',
  symbol: '記号(!@#$%...)',
  sameAsCurrent: '現在のパスワードと異なる',
};

const POLICY_CHECKS: PasswordPolicyViolationType[] = [
  'minLength',
  'maxLength',
  'uppercase',
  'lowercase',
  'digit',
  'symbol',
];

const RequiredPasswordChange: React.FC = () => {
  const navigate = useNavigate();
  const { logout, clearRequirePasswordChange } = useAuth();

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [apiError, setApiError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Real-time password policy validation
  const policyResult = validatePasswordPolicy(newPassword);
  const satisfiedPolicies = new Set(
    POLICY_CHECKS.filter((p) => !policyResult.violations.includes(p))
  );

  // Password match check
  const passwordsMatch = newPassword === confirmPassword;
  const hasConfirmInput = confirmPassword.length > 0;
  const isFormValid = policyResult.valid && passwordsMatch && hasConfirmInput;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setApiError('');

    if (!isFormValid) {
      return;
    }

    setIsLoading(true);

    try {
      const requestBody: ChangePasswordRequest = {
        newPassword,
        forceChange: true,
      };

      await apiClient.put('/api/v1/auth/password', requestBody);
      clearRequirePasswordChange();
      navigate('/files');
    } catch (error: unknown) {
      setApiError(getErrorMessage(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="hero-gradient">
      <div className="hero-card" style={{ maxWidth: '480px' }}>
        <div className="hero-card__header">
          <div className="hero-card__logo">
            <MkdfLogo height={28} />
            <span className="header__brand-divider" style={{ margin: '0 8px', opacity: 0.3 }}>|</span>
            <DFFilesLogo size={24} variant="light" />
          </div>
          <div className="hero-card__tagline">パスワード変更</div>
        </div>

        <div className="hero-card__body">
          <div className="alert alert--warning mb-4" role="alert">
            セキュリティのため、パスワードの変更が必要です
          </div>

          {apiError && (
            <div className="alert alert--error mb-4" role="alert">
              {apiError}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="newPassword" className="form-label">新しいパスワード</label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isLoading}
                placeholder="新しいパスワードを入力"
                autoComplete="new-password"
                className="form-input"
              />
            </div>

            {/* Password strength indicator */}
            <div className="policy-list mb-4">
              {POLICY_CHECKS.map((policy) => (
                <div
                  key={policy}
                  data-testid="policy-item"
                  className={`policy-item ${satisfiedPolicies.has(policy) ? 'policy-item--satisfied' : ''}`}
                >
                  <span className="policy-item__icon">
                    {satisfiedPolicies.has(policy) ? '✓' : '○'}
                  </span>
                  {POLICY_LABELS[policy]}
                </div>
              ))}
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">新しいパスワード（確認）</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isLoading}
                placeholder="パスワードを再入力"
                autoComplete="new-password"
                className={`form-input ${hasConfirmInput && !passwordsMatch ? 'form-input--error' : ''}`}
              />
              {hasConfirmInput && !passwordsMatch && (
                <p className="form-error">パスワードが一致しません</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading || !isFormValid}
              className="btn btn--primary btn--lg btn--block mb-4"
            >
              {isLoading ? '変更中...' : 'パスワードを変更'}
            </button>
          </form>

          <div style={{ textAlign: 'center' }}>
            <button
              onClick={handleLogout}
              className="btn btn--secondary btn--sm"
            >
              ログアウト
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequiredPasswordChange;
